import PrimaryButton from "components/buttons/primary-button";
import Dropdown from "components/inputs/dropdown";
import TextArea from "components/inputs/text-area";
import TextDropdown from "components/inputs/text-dropdown";
import TextInput from "components/inputs/text-input";
import Toast from "components/toast";
import Typography from "components/typography";
import { Countries } from "constants/countries";
import { MusicGenres } from "constants/genres";
import React, { useMemo, useState } from "react";
import api from "services/axiosInstance";

import { GetActiveArtist } from "services/user/getUser";
import { UpdateArtistData } from "services/user/setUser";
import styled from "styled-components";

const ArtistProfile = () => {
  const Artist = GetActiveArtist();

  const [artistData, setArtistData] = useState({ ...Artist });
  const [saving, setSaving] = useState(false);

  const ChangeArtistData = (key: string, value: string | string[]) => {
    setArtistData({ ...artistData, [key]: value });
  };

  const inputDisabled = useMemo(() => {
    if (Artist?.id) {
      return false;
    }
    return true;
  }, [Artist?.id]);

  const OnSaveChanges = async () => {
    try {
      if (Artist?.id === undefined) {
        Toast({ error: "Cannot update artist profile" });
        return;
      }

      setSaving(true);

      const body = { ...artistData };
      if (body.phoneNumber === "") {
        delete body.phoneNumber;
      }

      const { data } = await api.patch(`/artists/${Artist?.id}`, body);

      UpdateArtistData(data.data);
      Toast({
        type: "success",
        message: "Artist profile has been updated successfully"
      });
    } catch (error) {
      Toast({ error: error });
    } finally {
      setSaving(false);
    }
  };

  const CountriesNames = useMemo(
    () => Countries.map((c) => c.country),
    [Countries]
  );

  return (
    <Container>
      <Typography size="2xl" weight="medium" mb="4">
        Artist Profile
      </Typography>
      <Grid>
        <TextInput
          placeholder="name"
          label="Artist Name"
          value={artistData.name}
          onChange={(e) => {
            ChangeArtistData("name", e.target.value);
          }}
          disabled={inputDisabled}
        />
        <TextInput
          placeholder="xyz@email.com"
          label="Email"
          value={artistData.email}
          onChange={(e) => {
            ChangeArtistData("email", e.target.value);
          }}
          disabled={inputDisabled}
        />
        <TextDropdown
          value={artistData?.country || ""}
          selectItem={(value) => {
            ChangeArtistData("country", value);
          }}
          label="Country"
          items={CountriesNames}
          placeholder="Country"
          disabled={inputDisabled}
        />
        <Spacer />
        <TextInput
          placeholder="+234000000"
          label="Phone Number"
          value={artistData.phoneNumber}
          onChange={(e) => {
            ChangeArtistData("phoneNumber", e.target.value);
          }}
          disabled={inputDisabled}
        />
        <TextInput
          placeholder="https://xyz.com"
          label="Website"
          value={artistData.website}
          onChange={(e) => {
            ChangeArtistData("website", e.target.value);
          }}
          disabled={inputDisabled}
        />
        <TextDropdown
          label="Genre"
          items={MusicGenres}
          value={
            artistData?.genres &&
            artistData.genres.length > 0 &&
            artistData.genres[0]
              ? artistData.genres[0]
              : ""
          }
          selectItem={(value: string) => {
            ChangeArtistData("genres", [value]);
          }}
          placeholder="Afrobeat"
          disabled={inputDisabled}
        />
        <Spacer />
      </Grid>
      <TextArea
        label="Bio"
        height="10rem"
        limit={1000}
        value={artistData.bio}
        onChange={(e) => {
          ChangeArtistData("bio", e.target.value);
        }}
        disabled={inputDisabled}
      />
      <SaveButton>
        <PrimaryButton
          text="Save Changes"
          onClick={OnSaveChanges}
          loading={saving}
          disabled={!Artist?.id}
        />
      </SaveButton>
    </Container>
  );
};

export default ArtistProfile;

const Container = styled.div`
  width: 100%;
  background-color: rgba(30, 30, 30, 0.5);
  border-radius: 0.8rem;
  backdrop-filter: blur(5px);
  padding: 2.4rem;
`;

const Grid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 2rem;
  grid-column-gap: 1em;
  margin-bottom: 2rem;
  @media only screen and (max-width: 769px) {
    grid-template-columns: 1fr;
  }
`;

const SaveButton = styled.div`
  width: fit-content;
  margin-top: 2.4rem;
  margin-left: auto;
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const Spacer = styled.div`
  display: none;
  @media only screen and (max-width: 769px) {
    height: 2.4rem;
    display: block;
  }
`;
